import { useRef, useEffect, ChangeEvent, memo, useCallback } from 'react'
import {
  BLUR_MODE,
  FOCUS_MODE,
  INPUT_MAX_HEIGHT,
  INPUT_MAX_HEIGHT_MOBILE
} from './consts'
import {
  getTextAreaPastedValue,
  scrollTextAreaToTopAndHideOverflow,
  setTextAreaHeight
} from './utils'
import { ITextAreaInputProps, TFocusMode } from './types'
import * as Styled from './styled'
import { useResponsive } from 'hooks/useResponsive'
import { MOBILE_SIZE_BREAKPOINT } from 'consts'

const TextAreaInput: React.FC<ITextAreaInputProps> = ({
  textAreaRef: externalRef,
  className,
  placeholder,
  value,
  hasErrorOutline,
  hasWarningOutline,
  shouldFocus = false,
  maxLength,
  maxHeight = INPUT_MAX_HEIGHT,
  maxHeightMobile = INPUT_MAX_HEIGHT_MOBILE,
  minHeight,
  lockedHeight, // If provided, textarea height is fixed and does not auto-resize
  autoComplete = 'on',
  hasBorderStyles = true,
  onChange,
  onBlur,
  onFocus,
  onScroll
}) => {
  const internalRef = useRef<HTMLTextAreaElement | null>(null)
  const textAreaRef = externalRef ?? internalRef
  const isMobile = useResponsive(MOBILE_SIZE_BREAKPOINT)

  const computedMaxHeight = isMobile ? maxHeightMobile : maxHeight
  const textareaMaxHeight = lockedHeight ?? computedMaxHeight

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value
    if (maxLength && value.length > maxLength) {
      return
    }
    onChange(value)
  }

  const applyTextAreaHeight = useCallback(
    (mode: TFocusMode) => {
      if (textAreaRef.current) {
        setTextAreaHeight(textAreaRef.current, {
          lockedHeight,
          maxHeight: textareaMaxHeight,
          mode
        })
      }
    },
    [lockedHeight, textareaMaxHeight, textAreaRef]
  )

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      event.preventDefault() // Prevent the default paste behavior

      const pastedValue = getTextAreaPastedValue(event, textAreaRef, maxLength)

      if (!pastedValue) return

      // Update the textarea value immediately so scrollHeight measurement is correct
      if (textAreaRef.current) {
        textAreaRef.current.value = pastedValue
      }

      onChange(pastedValue)
      applyTextAreaHeight(FOCUS_MODE)
    },
    [maxLength, textAreaRef, onChange, applyTextAreaHeight]
  )

  const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    handleChange(event)
    //Dynamically adjust height only if not locked
    if (textAreaRef.current && !lockedHeight) {
      applyTextAreaHeight(FOCUS_MODE)
    }
  }

  useEffect(() => {
    if (textAreaRef.current) {
      if (shouldFocus) {
        textAreaRef.current.focus()
      }
      applyTextAreaHeight(shouldFocus ? FOCUS_MODE : BLUR_MODE)
    }
  }, [shouldFocus, applyTextAreaHeight, textAreaRef])

  return (
    <Styled.TextAreaInputWrapper
      name={'text-area-input'}
      className={className}
      rows={1}
      autoComplete={autoComplete}
      ref={textAreaRef}
      placeholder={placeholder}
      value={value}
      $isError={hasErrorOutline}
      $isWarning={hasWarningOutline}
      $hasBorderStyles={hasBorderStyles}
      $maxHeight={textareaMaxHeight}
      $minHeight={minHeight}
      onChange={handleInputChange}
      onFocus={e => {
        onFocus?.(e)
        applyTextAreaHeight(FOCUS_MODE)
      }}
      onBlur={e => {
        onBlur?.(e)
        scrollTextAreaToTopAndHideOverflow(textAreaRef)
      }}
      onScroll={onScroll}
      onPaste={handlePaste}
    />
  )
}

export default memo(TextAreaInput)
